import { useState, useRef, useCallback, useEffect } from 'react';

export interface UseRecorderReturn {
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  recordingBlob: Blob | null;
  recordingUrl: string | null;
  clearRecording: () => void;
  error: string | null;
  /** Live MediaStream for camera preview (non-null while recording with video) */
  stream: MediaStream | null;
  /** Whether the current/last recording includes video */
  hasVideo: boolean;
}

/** Video MIME types in preference order */
const VIDEO_MIME_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
];

/** Audio-only MIME types as fallback */
const AUDIO_MIME_TYPES = [
  'audio/webm',
  'audio/mp4',
];

function pickMimeType(types: string[]): string {
  for (const t of types) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return ''; // let browser choose
}

/**
 * Hook for recording video+audio via the MediaRecorder API.
 * Requests camera + microphone by default. Falls back to audio-only
 * if camera access is denied.
 */
export function useRecorder(): UseRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasVideo, setHasVideo] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const urlRef = useRef<string | null>(null);

  // Revoke previous object URL
  const revokeUrl = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Recording is not supported in this browser');
      return;
    }

    let mediaStream: MediaStream;
    let videoEnabled = false;

    // Try video + audio first
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: {
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      videoEnabled = true;
    } catch (videoErr) {
      // If video specifically was denied/not found, fall back to audio-only
      if (videoErr instanceof Error && (videoErr.name === 'NotAllowedError' || videoErr.name === 'NotFoundError' || videoErr.name === 'OverconstrainedError')) {
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: false,
              autoGainControl: false,
            },
          });
          videoEnabled = false;
        } catch (audioErr) {
          let message = 'Failed to access camera/microphone';
          if (audioErr instanceof Error) {
            if (audioErr.name === 'NotAllowedError') {
              message = 'Camera and microphone permission denied';
            } else if (audioErr.name === 'NotFoundError') {
              message = 'No camera or microphone found';
            } else {
              message = audioErr.message;
            }
          }
          setError(message);
          return;
        }
      } else {
        let message = 'Failed to access camera/microphone';
        if (videoErr instanceof Error) {
          message = videoErr.message;
        }
        setError(message);
        return;
      }
    }

    streamRef.current = mediaStream;
    setStream(mediaStream);
    setHasVideo(videoEnabled);

    // Pick MIME type
    const mimeType = videoEnabled
      ? pickMimeType(VIDEO_MIME_TYPES)
      : pickMimeType(AUDIO_MIME_TYPES);

    const recorder = new MediaRecorder(mediaStream, mimeType ? { mimeType } : undefined);
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, {
        type: recorder.mimeType || (videoEnabled ? 'video/webm' : 'audio/webm'),
      });
      setRecordingBlob(blob);
      revokeUrl();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setRecordingUrl(url);
      setIsRecording(false);

      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        setStream(null);
      }
    };

    recorder.onerror = () => {
      setError('Recording failed');
      setIsRecording(false);
    };

    recorder.start();
    setIsRecording(true);
  }, [revokeUrl]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const clearRecording = useCallback(() => {
    revokeUrl();
    setRecordingBlob(null);
    setRecordingUrl(null);
    setError(null);
  }, [revokeUrl]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    recordingBlob,
    recordingUrl,
    clearRecording,
    error,
    stream,
    hasVideo,
  };
}
